
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Line
} from 'recharts';
import { Target, ArrowUp, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface BenchmarkData {
  subject: string;
  userScore: number;
  expertBenchmark: number;
  percentile: number;
  gap: number;
}

interface SubjectPerformance {
  total: number;
  count: number;
  scores: number[];
}

interface PerformanceRecord {
  subject: string;
  score: number;
  created_at: string;
  user_id: string;
}

export const BenchmarkAnalysis = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const { isMobile, screenSize } = useIsMobile();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id ?? null);
    };
    fetchUser();
  }, []);

  const { data: benchmarkData, isLoading, error } = useQuery({
    queryKey: ['benchmarkAnalysis', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User not authenticated');

      const { data: performanceData, error: performanceError } = await supabase
        .from('performance_history')
        .select('subject, score, created_at, user_id')
        .eq('user_id', userId);

      if (performanceError) throw performanceError;

      // Define expert benchmarks for different subjects
      const expertBenchmarks: Record<string, number> = {
        'Computer Science': 90,
        'Engineering': 88,
        'Data Science': 90,
        'Machine Learning': 92,
        'Web Development': 85,
        'Programming': 88,
        'Artificial Intelligence': 92,
        'Computer Science Engineering': 90,
        'Big Data': 89,
        'Modern History': 85,
        'Geography': 85,
        'World Politics': 85,
        'Biology': 87,
        'Networking': 86,
        'Reasoning': 85,
        'History': 85,
        'Mathematics': 88,
        'Physics': 87,
        'Chemistry': 86
      };

      // Calculate averages per subject
      const subjectPerformance = (performanceData || []).reduce((acc: Record<string, SubjectPerformance>, curr: PerformanceRecord) => {
        if (!curr.subject) return acc;
        
        if (!acc[curr.subject]) {
          acc[curr.subject] = {
            total: 0,
            count: 0,
            scores: []
          };
        }
        
        const score = curr.score || 0;
        acc[curr.subject].total += score;
        acc[curr.subject].count += 1;
        acc[curr.subject].scores.push(score);
        
        return acc;
      }, {});

      // Transform data for visualization
      return Object.entries(subjectPerformance).map(([subject, data]): BenchmarkData => {
        const userScore = data.total / data.count;
        const expertBenchmark = expertBenchmarks[subject] || 85;
        
        // Calculate percentile based on position in sorted scores
        const sortedScores = [...data.scores].sort((a, b) => b - a); // Sort in descending order
        const position = sortedScores.findIndex(score => score <= userScore);
        const percentile = position !== -1 
          ? ((sortedScores.length - position) / sortedScores.length) * 100 
          : 100;
        
        const gap = expertBenchmark - userScore;
        
        return {
          subject,
          userScore,
          expertBenchmark,
          percentile: Math.round(percentile), // Round to nearest integer
          gap: gap > 0 ? gap : 0 // Ensure gap is never negative
        };
      }).sort((a, b) => b.gap - a.gap);
    },
    enabled: !!userId,
  });

  if (error) {
    console.error('Benchmark analysis error:', error);
    toast.error('Failed to load benchmark analysis');
    return null;
  }

  if (isLoading) {
    return (
      <Card className={isMobile ? "p-4" : "p-6"}>
        <CardHeader>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (!benchmarkData?.length) {
    return (
      <Card className={isMobile ? "p-4" : "p-6"}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-base" : "text-lg"}`}>
            <Target className="w-5 h-5 text-purple-500" />
            Expert Benchmark Analysis
          </CardTitle>
          <CardDescription>
            Complete more quizzes to see how you compare with expert-level performance.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // For mobile devices, limit the number of subjects shown in the chart
  const displayData = isMobile ? 
    benchmarkData.slice(0, screenSize === 'xs' ? 4 : 6) : 
    benchmarkData;

  return (
    <Card className={isMobile ? "p-4" : "p-6"}>
      <CardHeader className={isMobile ? "p-0 pb-4" : ""}>
        <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-base" : "text-lg"}`}>
          <Target className="w-5 h-5 text-purple-500" />
          Expert Benchmark Analysis
        </CardTitle>
        <CardDescription className={isMobile ? "text-xs" : "text-sm"}>
          Compare your performance with expert-level benchmarks
        </CardDescription>
      </CardHeader>
      <CardContent className={isMobile ? "p-0" : ""}>
        <div className={`${isMobile ? "h-[250px]" : "h-[300px]"} mb-6`}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={displayData}
              margin={isMobile ? 
                { top: 15, right: 5, bottom: 60, left: 5 } : 
                { top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="subject" 
                angle={-45} 
                textAnchor="end" 
                height={80} 
                tick={{ fontSize: isMobile ? 9 : 12 }}
                interval={0}
              />
              <YAxis 
                domain={[0, 100]} 
                tick={{ fontSize: isMobile ? 9 : 12 }}
              />
              <Tooltip
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ccc',
                  fontSize: isMobile ? 10 : 12 
                }}
                formatter={(value: number, name: string) => [
                  `${value.toFixed(1)}%`,
                  name === 'userScore' ? 'Your Score' : 'Expert Benchmark'
                ]}
              />
              <Bar
                dataKey="userScore"
                fill="#8B5CF6"
                name="Your Score"
                radius={[4, 4, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="expertBenchmark"
                stroke="#EF4444"
                strokeWidth={2}
                name="Expert Benchmark"
                dot={{ fill: '#EF4444', r: isMobile ? 2 : 4 }}
              />
              <ReferenceLine 
                y={85} 
                stroke="#10B981" 
                strokeDasharray="3 3" 
                label={{
                  value: "Mastery Goal (85%)", 
                  fill: "#10B981", 
                  fontSize: isMobile ? 9 : 12,
                  position: "insideBottomRight"
                }} 
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className={`space-y-4 ${isMobile ? "mt-2" : "mt-4"}`}>
          <h3 className={`font-medium text-gray-700 ${isMobile ? "text-sm" : "text-base"}`}>
            Performance Gaps & Recommendations
          </h3>
          <div className="grid gap-3">
            {(isMobile ? benchmarkData.slice(0, 3) : benchmarkData.slice(0, 5)).map((data) => (
              <div
                key={data.subject}
                className={`${isMobile ? "p-2" : "p-4"} bg-gray-50 rounded-lg border-l-4 border-purple-500`}
              >
                <div className={`flex items-center justify-between ${isMobile ? "mb-1" : "mb-2"}`}>
                  <h4 className={`font-medium ${isMobile ? "text-xs" : "text-sm"}`}>{data.subject}</h4>
                  <div className={`flex items-center gap-2 ${isMobile ? "text-[10px]" : "text-xs"}`}>
                    <span className={`px-2 py-1 rounded-full ${
                      data.userScore >= data.expertBenchmark ? 'bg-green-100 text-green-800' :
                      data.gap <= 10 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {data.userScore >= data.expertBenchmark ? (
                        'Expert Level!'
                      ) : (
                        <span className="flex items-center gap-1">
                          <ArrowUp className={`${isMobile ? "w-2 h-2" : "w-3 h-3"}`} />
                          {data.gap.toFixed(1)}% to expert
                        </span>
                      )}
                    </span>
                    <span className="text-gray-500">
                      {isMobile ? `${data.percentile}%` : `Percentile: ${data.percentile}%`}
                    </span>
                  </div>
                </div>
                {data.gap > 0 && !isMobile && (
                  <div className="flex items-start gap-2 text-sm text-gray-600 mt-2">
                    <AlertCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    <p>
                      Focus on advanced {data.subject} concepts and practice with expert-level 
                      questions to bridge the {data.gap.toFixed(1)}% gap to expert performance.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
